import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';

interface StatsCardProps {
  icon: LucideIcon;
  label: string;
  value: number | string;
  iconColor?: string;
  iconBg?: string;
  href?: string;
  trend?: {
    direction: 'up' | 'down';
    label: string;
  };
  accent?: 'blue' | 'green' | 'orange' | 'red' | 'indigo' | 'purple';
}

const accentStyles = {
  blue: 'from-blue-500/10 to-blue-600/5 dark:from-blue-500/20 dark:to-blue-600/10 border-blue-200/50 dark:border-blue-500/20',
  green: 'from-green-500/10 to-green-600/5 dark:from-green-500/20 dark:to-green-600/10 border-green-200/50 dark:border-green-500/20',
  orange: 'from-orange-500/10 to-orange-600/5 dark:from-orange-500/20 dark:to-orange-600/10 border-orange-200/50 dark:border-orange-500/20',
  red: 'from-red-500/10 to-red-600/5 dark:from-red-500/20 dark:to-red-600/10 border-red-200/50 dark:border-red-500/20',
  indigo: 'from-indigo-500/10 to-indigo-600/5 dark:from-indigo-500/20 dark:to-indigo-600/10 border-indigo-200/50 dark:border-indigo-500/20',
  purple: 'from-purple-500/10 to-purple-600/5 dark:from-purple-500/20 dark:to-purple-600/10 border-purple-200/50 dark:border-purple-500/20',
};

export function StatsCard({ icon: Icon, label, value, iconColor, iconBg, href, trend, accent = 'blue' }: StatsCardProps) {
  const content = (
    <div className={cn(
      'relative overflow-hidden rounded-xl border bg-gradient-to-br p-5 transition-all hover:shadow-lg hover:-translate-y-0.5 h-full',
      href && 'cursor-pointer',
      accentStyles[accent]
    )}>
      <div className="flex items-start justify-between">
        <div className={cn('h-11 w-11 rounded-xl flex items-center justify-center', iconBg || 'bg-blue-100 dark:bg-blue-900/50')}>
          <Icon className={cn('h-5 w-5', iconColor || 'text-blue-600 dark:text-blue-400')} />
        </div>
        {trend && (
          <span className={cn(
            'text-xs font-semibold px-2 py-0.5 rounded-full',
            trend.direction === 'up'
              ? 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/50'
              : 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/50'
          )}>
            {trend.direction === 'up' ? '\u2191' : '\u2193'} {trend.label}
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-5xl font-black tracking-tight text-gray-900 dark:text-white">
          {value}
        </p>
        <p className="text-sm font-semibold text-muted-foreground dark:text-foreground mt-1 uppercase tracking-wide">{label}</p>
      </div>
    </div>
  );

  if (href) {
    return <Link to={href} className="block h-full">{content}</Link>;
  }

  return content;
}
